/**
 * Game AI for Revolving Chain Four
 * Handles AI decision making for both drop and shift phases
 *
 * Enhanced AI with minimax algorithm, alpha-beta pruning and improved evaluation.
 * Works with any board size (rows/cols are always derived from the board array).
 */

// Constants for evaluation scores - used by the minimax evaluation function
const SCORES = {
  WIN: 1000000,      // Win score (very high)
  POTENTIAL_WIN: 1000, // Near-win position
  THREE_IN_LINE: 100,  // Three chips in a line with an open end
  TWO_IN_LINE: 10,     // Two chips in a line with open ends
  CENTER_CONTROL: 5,   // Control of center column
  ADVANCED_SHIFT: 50,  // Good shift that creates advantage
};

/**
 * Find an immediate winning move or blocking move
 * @param {Array} board - Current game board
 * @param {String} player - Player to check for win ('red' or 'blue')
 * @param {Function} checkForWin - Function to check for a win
 * @returns {Number|null} Column index for immediate win/block or null if none
 */
const findImmediateWinOrBlock = (board, player, checkForWin) => {
  const rows = board.length;
  const cols = board[0].length;

  // Check each column
  for (let col = 0; col < cols; col++) {
    // Skip full columns
    if (board[0][col] !== null) continue;

    // Find row where piece would land
    let row = rows - 1;
    while (row >= 0 && board[row][col] !== null) row--;
    if (row < 0) continue; // This shouldn't happen if we checked column fullness

    // Try placing a piece here
    const testBoard = board.map(r => [...r]);
    testBoard[row][col] = player;

    // Check if this creates a win
    if (checkForWin(testBoard, row, col, player)) {
      return col;
    }
  }
  return null;
};

/**
 * Shift a row in the given direction, ejecting the edge chip instead of wrapping it
 * @param {Array} board - Board to modify
 * @param {Number} rowIndex - Row to shift
 * @param {String} direction - 'left' or 'right'
 * @returns {String|null} Color of the ejected chip, or null if the edge cell was empty
 */
const shiftRow = (board, rowIndex, direction) => {
  const row = board[rowIndex];

  if (direction === 'left') {
    // Remove first element - it's ejected off the left edge
    const ejected = row.shift();
    row.push(null);
    return ejected;
  } else {
    // Remove last element - it's ejected off the right edge
    const ejected = row.pop();
    row.unshift(null);
    return ejected;
  }
};

/**
 * Apply gravity to the board after a shift
 * @param {Array} board - Board to modify
 * @returns {Array} Array of falling chip data
 */
const applyGravity = (board) => {
  const rows = board.length;
  const cols = board[0].length;
  const fallingChips = [];

  // Check each column
  for (let col = 0; col < cols; col++) {
    // Start from the bottom up (excluding bottom row)
    for (let row = rows - 2; row >= 0; row--) {
      // If this cell has a chip and there's empty space below
      if (board[row][col] && board[row + 1][col] === null) {
        // Find how far it will fall
        let newRow = row + 1;
        while (newRow + 1 < rows && board[newRow + 1][col] === null) {
          newRow++;
        }

        // Record the move for animation
        fallingChips.push({
          fromRow: row,
          toRow: newRow,
          col,
          color: board[row][col]
        });

        // Update the board
        board[newRow][col] = board[row][col];
        board[row][col] = null;
      }
    }
  }

  return fallingChips;
};

/**
 * Check if there is a win (of any color) after shifting a row
 * @param {Array} board - Board after shifting
 * @param {String} player - Player to check for win
 * @param {Function} checkForWin - Function to check for win
 * @returns {Boolean} True if a win is found for the given player
 */
const checkForWinAfterShift = (board, player, checkForWin) => {
  const rows = board.length;
  const cols = board[0].length;
  // Check entire board for a win
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (board[r][c] === player && checkForWin(board, r, c, player)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Check if the board is full (draw)
 * @param {Array} board - Current board
 * @returns {Boolean} True if board is full
 */
const isBoardFull = (board) => {
  // Check if top row is full
  return board[0].every(cell => cell !== null);
};

/**
 * Minimax algorithm with alpha-beta pruning
 * @param {Array} board - Current board state
 * @param {Number} depth - Search depth
 * @param {Number} alpha - Alpha value for pruning
 * @param {Number} beta - Beta value for pruning
 * @param {Boolean} isMaximizing - Whether current player is maximizing
 * @param {String} aiPlayer - AI player color
 * @param {String} humanPlayer - Human player color
 * @param {Function} checkForWin - Win checking function
 * @returns {Number} Score of the position
 */
const minimax = (board, depth, alpha, beta, isMaximizing, aiPlayer, humanPlayer, checkForWin) => {
  const rows = board.length;
  const cols = board[0].length;

  // Terminal case - depth limit or game end
  if (depth === 0) {
    return evaluateBoard(board, aiPlayer, humanPlayer, checkForWin);
  }

  // Check for a win for either player
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (board[r][c] === aiPlayer && checkForWin(board, r, c, aiPlayer)) {
        return SCORES.WIN; // AI win
      }
      if (board[r][c] === humanPlayer && checkForWin(board, r, c, humanPlayer)) {
        return -SCORES.WIN; // Human win
      }
    }
  }

  // Check if board is full (draw)
  if (isBoardFull(board)) {
    return 0;
  }

  if (isMaximizing) {
    let maxEval = -Infinity;

    // Try all valid columns
    for (let col = 0; col < cols; col++) {
      // Skip full columns
      if (board[0][col] !== null) continue;

      // Find landing row
      let row = rows - 1;
      while (row >= 0 && board[row][col] !== null) row--;
      if (row < 0) continue;

      // Make move
      const newBoard = board.map(r => [...r]);
      newBoard[row][col] = aiPlayer;

      // Recursive evaluation
      const evalScore = minimax(newBoard, depth - 1, alpha, beta, false, aiPlayer, humanPlayer, checkForWin);

      maxEval = Math.max(maxEval, evalScore);
      alpha = Math.max(alpha, evalScore);
      if (beta <= alpha) break; // Alpha-beta pruning
    }

    return maxEval;
  } else {
    let minEval = Infinity;

    // Try all valid columns
    for (let col = 0; col < cols; col++) {
      // Skip full columns
      if (board[0][col] !== null) continue;

      // Find landing row
      let row = rows - 1;
      while (row >= 0 && board[row][col] !== null) row--;
      if (row < 0) continue;

      // Make move
      const newBoard = board.map(r => [...r]);
      newBoard[row][col] = humanPlayer;

      // Recursive evaluation
      const evalScore = minimax(newBoard, depth - 1, alpha, beta, true, aiPlayer, humanPlayer, checkForWin);

      minEval = Math.min(minEval, evalScore);
      beta = Math.min(beta, evalScore);
      if (beta <= alpha) break; // Alpha-beta pruning
    }

    return minEval;
  }
};

/**
 * Evaluate the current board position
 * @param {Array} board - Current board
 * @param {String} aiPlayer - AI player color
 * @param {String} humanPlayer - Human player color
 * @returns {Number} Score of the position
 */
const evaluateBoard = (board, aiPlayer, humanPlayer) => {
  let score = 0;

  // Check horizontal segments
  score += evaluateLines(board, aiPlayer, humanPlayer, 'horizontal');

  // Check vertical segments
  score += evaluateLines(board, aiPlayer, humanPlayer, 'vertical');

  // Check diagonal segments
  score += evaluateLines(board, aiPlayer, humanPlayer, 'diagonal');

  // Give bonus for center column control (strategically valuable)
  const rows = board.length;
  const cols = board[0].length;
  const centerCol = Math.floor(cols / 2);
  for (let row = 0; row < rows; row++) {
    if (board[row][centerCol] === aiPlayer) {
      score += SCORES.CENTER_CONTROL;
    } else if (board[row][centerCol] === humanPlayer) {
      score -= SCORES.CENTER_CONTROL;
    }
  }

  return score;
};

/**
 * Evaluate all lines of a particular direction on the board
 * @param {Array} board - Current board
 * @param {String} aiPlayer - AI player color
 * @param {String} humanPlayer - Human player color
 * @param {String} direction - Direction to evaluate ('horizontal', 'vertical', 'diagonal')
 * @returns {Number} Score for lines in this direction
 */
const evaluateLines = (board, aiPlayer, humanPlayer, direction) => {
  let score = 0;
  const rows = board.length;
  const cols = board[0].length;

  // Define how to check lines based on direction
  let lines = [];

  if (direction === 'horizontal') {
    // Horizontal lines
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col <= cols - 4; col++) {
        lines.push([{ row, col }, { row, col: col + 1 }, { row, col: col + 2 }, { row, col: col + 3 }]);
      }
    }
  } else if (direction === 'vertical') {
    // Vertical lines
    for (let col = 0; col < cols; col++) {
      for (let row = 0; row <= rows - 4; row++) {
        lines.push([{ row, col }, { row: row + 1, col }, { row: row + 2, col }, { row: row + 3, col }]);
      }
    }
  } else if (direction === 'diagonal') {
    // Diagonal lines (down-right)
    for (let row = 0; row <= rows - 4; row++) {
      for (let col = 0; col <= cols - 4; col++) {
        lines.push([
          { row, col },
          { row: row + 1, col: col + 1 },
          { row: row + 2, col: col + 2 },
          { row: row + 3, col: col + 3 }
        ]);
      }
    }

    // Diagonal lines (down-left)
    for (let row = 0; row <= rows - 4; row++) {
      for (let col = 3; col < cols; col++) {
        lines.push([
          { row, col },
          { row: row + 1, col: col - 1 },
          { row: row + 2, col: col - 2 },
          { row: row + 3, col: col - 3 }
        ]);
      }
    }
  }

  // Evaluate each line
  for (const line of lines) {
    let aiCount = 0;
    let humanCount = 0;
    let emptyCount = 0;

    for (const pos of line) {
      const cell = board[pos.row][pos.col];
      if (cell === aiPlayer) aiCount++;
      else if (cell === humanPlayer) humanCount++;
      else emptyCount++;
    }

    // Line has only AI pieces and empty spaces
    if (aiCount > 0 && humanCount === 0) {
      if (aiCount === 3 && emptyCount === 1) score += SCORES.POTENTIAL_WIN;
      else if (aiCount === 2 && emptyCount === 2) score += SCORES.TWO_IN_LINE;
      else if (aiCount === 1 && emptyCount === 3) score += 1; // Small bonus for single piece
    }

    // Line has only human pieces and empty spaces
    if (humanCount > 0 && aiCount === 0) {
      if (humanCount === 3 && emptyCount === 1) score -= SCORES.POTENTIAL_WIN;
      else if (humanCount === 2 && emptyCount === 2) score -= SCORES.TWO_IN_LINE;
      else if (humanCount === 1 && emptyCount === 3) score -= 1;
    }
  }

  return score;
};

/**
 * Determines the best column for the AI to drop a chip of its own color
 * @param {Array} board - Current game board state (2D array)
 * @param {String} aiPlayer - AI player color ('red' or 'blue')
 * @param {String} humanPlayer - Human player color ('red' or 'blue')
 * @param {Function} checkForWin - Function for checking if a move leads to victory
 * @returns {Number} Best column to drop a chip
 */
export const selectAiDropColumn = (board, aiPlayer, humanPlayer, difficulty, checkForWin) => {
  return selectColumnForColor(board, aiPlayer, aiPlayer, humanPlayer, checkForWin);
};

/**
 * Determines the best column to drop a chip of an arbitrary color, evaluated from the
 * AI's true perspective. Used both for the AI's normal turn chip and for a forced
 * placement of a received (hand) chip, which may be either color.
 * @param {Array} board - Current game board state (2D array)
 * @param {String} colorToPlace - Color of the chip that must be placed
 * @param {String} aiPlayer - The AI's true color
 * @param {String} humanPlayer - The human's true color
 * @param {Function} checkForWin - Function for checking if a move leads to victory
 * @returns {Number} Best column to drop the chip into
 */
export const selectColumnForColor = (board, colorToPlace, aiPlayer, humanPlayer, checkForWin) => {
  const rows = board.length;
  const cols = board[0].length;

  const validCols = [];
  for (let col = 0; col < cols; col++) {
    if (board[0][col] !== null) continue;
    let row = rows - 1;
    while (row >= 0 && board[row][col] !== null) row--;
    if (row < 0) continue;
    validCols.push({ col, row });
  }

  if (validCols.length === 0) return Math.floor(cols / 2);

  // Never hand the opponent an immediate win by placing their color
  const safeCols = validCols.filter(({ col, row }) => {
    const testBoard = board.map(r => [...r]);
    testBoard[row][col] = colorToPlace;
    const wins = checkForWin(testBoard, row, col, colorToPlace);
    return !(wins && colorToPlace === humanPlayer);
  });

  const candidateCols = safeCols.length > 0 ? safeCols : validCols;

  if (colorToPlace === aiPlayer) {
    // Take an immediate win for ourselves if placing our own color completes one
    for (const { col, row } of candidateCols) {
      const testBoard = board.map(r => [...r]);
      testBoard[row][col] = colorToPlace;
      if (checkForWin(testBoard, row, col, colorToPlace)) {
        return col;
      }
    }

    // Otherwise block an immediate win the opponent would otherwise get next turn
    const blockingMove = findImmediateWinOrBlock(board, humanPlayer, checkForWin);
    if (blockingMove !== null && candidateCols.some(c => c.col === blockingMove)) {
      return blockingMove;
    }
  }

  // Otherwise evaluate the resulting position from the AI's true perspective
  let bestCol = candidateCols[0].col;
  let bestScore = -Infinity;

  for (const { col, row } of candidateCols) {
    const testBoard = board.map(r => [...r]);
    testBoard[row][col] = colorToPlace;

    const score = minimax(testBoard, 3, -Infinity, Infinity, colorToPlace !== aiPlayer, aiPlayer, humanPlayer, checkForWin);

    if (score > bestScore) {
      bestScore = score;
      bestCol = col;
    }
  }

  return bestCol;
};

/**
 * Determines the best row and direction for the AI to shift
 * @param {Array} board - Current game board state (2D array)
 * @param {String} currentPlayer - The player whose shift this is (the AI)
 * @param {String} aiDifficulty - Difficulty level (currently unused, always "hard")
 * @param {Function} checkForWin - Function to check for wins
 * @returns {Object} Object with row and direction
 */
export const selectAiShiftMove = (board, currentPlayer, aiDifficulty, checkForWin) => {
  // In our new implementation, currentPlayer is the AI player
  const aiPlayer = currentPlayer;
  const humanPlayer = currentPlayer === 'red' ? 'blue' : 'red';
  const rows = board.length;

  // Only consider rows that have chips - the rules require shifting a non-empty row
  const rowsWithChips = [];
  for (let row = 0; row < rows; row++) {
    if (board[row].some(cell => cell !== null)) {
      rowsWithChips.push(row);
    }
  }

  // No rows with chips, cannot shift (should not happen in practice - a chip was
  // just dropped this turn, so at least one row always has a chip during shift phase)
  if (rowsWithChips.length === 0) {
    return { row: 0, direction: 'left' };
  }

  // Try each possible shift and evaluate
  let bestMove = { row: rowsWithChips[0], direction: 'left' };
  let bestScore = -Infinity;

  for (const row of rowsWithChips) {
    for (const direction of ['left', 'right']) {
      // Try this shift
      const newBoard = board.map(r => [...r]);
      shiftRow(newBoard, row, direction);
      applyGravity(newBoard);

      // Use minimax to evaluate this position
      const score = minimax(newBoard, 3, -Infinity, Infinity, false, aiPlayer, humanPlayer, checkForWin);

      // Check if this move results in a win for the AI
      const winAfterShift = checkForWinAfterShift(newBoard, aiPlayer, checkForWin);
      if (winAfterShift) {
        return { row, direction };
      }

      // Update best move if better score is found
      if (score > bestScore) {
        bestScore = score;
        bestMove = { row, direction };
      }
    }
  }

  return bestMove;
};

/**
 * Export a function to check for win conditions
 */
export const checkForWin = (board, row, col, player) => {
  const rows = board.length;
  const cols = board[0].length;

  const directions = [
    [0, 1],  // horizontal
    [1, 0],  // vertical
    [1, 1],  // diagonal down-right
    [1, -1], // diagonal down-left
  ];

  for (const [dx, dy] of directions) {
    let count = 1;  // Start with 1 (the piece just placed)
    const positions = [{row, col}]; // Start with the current position

    // Check in both directions along the current line
    for (const factor of [1, -1]) {
      let r = row + factor * dx;
      let c = col + factor * dy;

      while (
        r >= 0 && r < rows &&
        c >= 0 && c < cols &&
        board[r][c] === player
      ) {
        positions.push({row: r, col: c});
        count++;
        r += factor * dx;
        c += factor * dy;
      }
    }

    if (count >= 4) {
      return positions; // Return winning positions
    }
  }

  return null; // No win found
};
